/**
 * Transcript accumulator for chained transcription sessions.
 *
 * Each WebSocket session streams a *cumulative* running transcript
 * (`setLive` — assign, never append). When a session ends (clean stop,
 * rotation, server max_duration, or an unexpected drop) its text is folded
 * into `committed` and the next session's cumulative text becomes the new
 * live tail. `fullText()` joins committed + live and never regresses across
 * commits — words the user has seen survive every seam.
 *
 * Rotation uses the provisional flow: `beginProvisional()` freezes the live
 * text as a committed segment immediately (so the next session's transcripts
 * don't clobber it), then `resolveProvisional()` upgrades it with the
 * server-polished `session_final` text when (if) it arrives.
 *
 * Drop seams may re-transcribe ~1s of already-transcribed audio (the replay
 * rewind prefers duplication over loss), so segments committed with reason
 * `drop_rescue` arm a word-overlap dedupe against the next live text.
 */

export type SegmentReason =
  | "stop"
  | "max_duration"
  | "rotation"
  | "drop_rescue";

export interface CommittedSegment {
  text: string;
  reason: SegmentReason;
  /** Still awaiting the session_final upgrade (rotation in flight). */
  provisional?: boolean;
}

export interface AccumulatorSnapshot {
  committed: CommittedSegment[];
  live: string;
}

const MAX_OVERLAP_WORDS = 20;
const MIN_OVERLAP_WORDS = 3;

function normalizeWord(word: string): string {
  return word.toLowerCase().replace(/[^\p{L}\p{N}']+/gu, "");
}

/**
 * Strip a re-transcribed prefix off `live` when it overlaps the tail of the
 * previous (drop-rescued) segment. Compares up to the last/first 20 words,
 * normalized; requires ≥3 matching words to elide — otherwise the join is
 * kept whole (duplication over loss, always).
 */
export function dedupeSeam(committedTail: string, live: string): string {
  const tailWords = committedTail.split(/\s+/).filter(Boolean);
  const liveWords = live.split(/\s+/).filter(Boolean);
  if (!tailWords.length || !liveWords.length) return live;

  const tailNorm = tailWords.slice(-MAX_OVERLAP_WORDS).map(normalizeWord);
  const liveNorm = liveWords.slice(0, MAX_OVERLAP_WORDS).map(normalizeWord);

  const maxK = Math.min(tailNorm.length, liveNorm.length);
  for (let k = maxK; k >= MIN_OVERLAP_WORDS; k--) {
    let match = true;
    for (let i = 0; i < k; i++) {
      if (tailNorm[tailNorm.length - k + i] !== liveNorm[i]) {
        match = false;
        break;
      }
    }
    if (match) {
      return liveWords.slice(k).join(" ");
    }
  }
  return live;
}

export class TranscriptAccumulator {
  private committed: CommittedSegment[] = [];
  private live = "";

  setLive(text: string): void {
    this.live = text;
  }

  getLive(): string {
    return this.live;
  }

  /**
   * Strip the re-transcribed overlap off `text` when the segment preceding
   * `index` was a drop rescue. Applied on every write path, not just on read:
   * committing the raw overlapping tail would bake the duplicate words into
   * `committed` permanently, and the next commit/rotation would surface them
   * in the transcript the user actually sends.
   */
  private dedupeAgainstPrevious(text: string, index: number): string {
    const previous = this.committed[index - 1];
    return previous?.reason === "drop_rescue"
      ? dedupeSeam(previous.text, text)
      : text;
  }

  /** Live text with any drop-rescue overlap already removed. */
  private dedupedLive(): string {
    const live = this.live.trim();
    if (!live) return "";
    return this.dedupeAgainstPrevious(live, this.committed.length);
  }

  /**
   * Fold the current live text into `committed` (empty live commits nothing)
   * and clear the live slot for the next session.
   */
  commitLive(reason: SegmentReason): void {
    const text = this.dedupedLive();
    this.live = "";
    if (text) {
      this.committed.push({ text, reason });
    }
  }

  /**
   * Rotation: freeze live as a provisional segment and return its index, or
   * null when there was nothing to freeze. `resolveProvisional` upgrades it
   * with the polished session_final text later.
   */
  beginProvisional(reason: SegmentReason): number | null {
    const text = this.dedupedLive();
    this.live = "";
    if (!text) return null;
    this.committed.push({ text, reason, provisional: true });
    return this.committed.length - 1;
  }

  /**
   * Refresh a provisional segment while its session drains — the stop-flush
   * can still refine the cumulative text before session_final lands.
   */
  updateProvisional(index: number, text: string): void {
    const segment = this.committed[index];
    if (!segment?.provisional) return;
    // The server sends its whole session transcript, so re-dedupe: the
    // overlap stripped at beginProvisional is present again in this text.
    const trimmed = this.dedupeAgainstPrevious(text.trim(), index);
    if (trimmed) this.committed[index] = { ...segment, text: trimmed };
  }

  /**
   * Upgrade a provisional segment with the authoritative session_final text.
   * An empty final keeps the provisional text — session_final can legitimately
   * differ (polish), but a blank final on text the user watched arrive would
   * violate the no-loss guarantee.
   */
  resolveProvisional(
    index: number,
    finalText: string,
    reason: SegmentReason,
  ): void {
    const segment = this.committed[index];
    if (!segment?.provisional) return;
    // Same re-dedupe as updateProvisional: session_final carries the whole
    // session, including audio the previous segment already transcribed.
    const text = this.dedupeAgainstPrevious(finalText.trim(), index);
    this.committed[index] = {
      text: text || segment.text,
      reason,
    };
  }

  /** Committed text + (seam-deduped) live tail. */
  fullText(): string {
    const parts = this.committed.map((segment) => segment.text);
    const live = this.dedupedLive();
    if (live) parts.push(live);
    return parts.join(" ");
  }

  hasText(): boolean {
    return this.committed.length > 0 || this.live.trim().length > 0;
  }

  snapshot(): AccumulatorSnapshot {
    return {
      committed: this.committed.map((segment) => ({ ...segment })),
      live: this.live,
    };
  }

  restore(snapshot: AccumulatorSnapshot): void {
    this.committed = snapshot.committed.map((segment) => ({ ...segment }));
    this.live = snapshot.live;
  }

  reset(): void {
    this.committed = [];
    this.live = "";
  }
}
